import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { bindActionCreators } from 'redux'
import { connect } from 'react-redux'
import { translate } from 'react-i18next'

import moment from 'moment'

import { renderToastWarn } from 'utils/ToastUtils'
import { ToastContainer } from 'react-toastify'
import Breadcrumb from 'component/Breadcrumb'
import Col from 'component/utils/Col'
import ContentHeader from 'component/utils/ContentHeader'
import DeleteModal from 'component/priceBanner/priceBannerConfig/DeleteModal'
import Grid from 'component/utils/Grid'
import Notification from 'component/utils/Notification'
import Pagination from 'component/pagination/Pagination'
import Row from 'component/utils/Row'
import SaveModal from 'component/priceBanner/priceBannerConfig/SaveModal'
import SuccessModal from 'component/Modal/SuccessModal'
import ErrorModal from 'component/utils/ErrorModal'
import Table from 'component/priceBanner/priceBannerConfig/Table'
import ViewModal from 'component/priceBanner/priceBannerConfig/ViewModal'
import Mask from 'vanilla-masker'

import * as parcelActions from 'action/parcel'
import * as pricebannerConfigActions from 'action/pricebannerConfig'
import * as pricefyActions from 'action/pricefy'

const CLOSE_MODAL = { open: false }

const DEFAULT_PAGINATION = {
  hasPagination: true,
  page: 1,
  size: 10,
}

const PriceBannerConfigContainer = ({
  auth,
  auth: {
    user: { dsToken },
  },
  count,
  current,
  error,
  dataSave,
  errorSave,
  fetch,
  fetchParcelList,
  fetchPricefyConfig,
  list,
  pages,
  parcelList,
  pricefyConfigError,
  pricefyConfigRecord,
  remove,
  save,
  translate,
}) => {
  const formattedDynamicList = pricefyConfigRecord.map((i) => ({
    value: i.codigo,
    label: i.rotulo,
  }))

  const [openSaveModal, setOpenSaveModal] = useState(false)
  const [openSuccessModal, setOpenSuccessModal] = useState({
    open: false,
    message: '',
  })
  const [openErrorModal, setOpenErrorModal] = useState({
    open: false,
    message: '',
  })
  const [openDeleteModal, setOpenDeleteModal] = useState({
    open: false,
    record: {},
  })
  const [openUpdateModal, setOpenUpdateModal] = useState({
    open: false,
    record: {},
  })
  const [openViewModal, setOpenViewModal] = useState({
    open: false,
    record: {},
  })
  const [pagination, setPagination] = useState(DEFAULT_PAGINATION)

  useEffect(() => {
    const actualError = error || pricefyConfigError

    if (actualError) renderToastWarn(actualError)
  }, [error, pricefyConfigError])

  useEffect(() => {
    if (errorSave && !dataSave) {
      setOpenErrorModal({ open: true, message: errorSave.message.dsMessage })
    } else if (dataSave && !errorSave) {
      setOpenSuccessModal({
        open: true,
        message: 'config.pricebannerConfig.updateSuccess',
      })
    }
  }, [dataSave, errorSave])

  useEffect(() => {
    handleFetch()
  }, [pagination])

  const handleFetch = async () => {
    const param = {
      ...pagination,
    }

    await fetch(dsToken, param)

    await fetchPricefyConfig(dsToken)

    await fetchParcelList(dsToken)
  }

  const renderBreadcrumb = () => {
    const steps = [
      { title: translate('mobile.home'), link: '/' },
      { title: translate('menu.configuration') },
      { title: translate('config.pricebannerConfig') },
    ]

    return <Breadcrumb steps={steps} />
  }

  const handleOpenSaveModal = (open) => {
    setOpenSaveModal(open)
  }

  const handleOpenDeleteModal = (newOpenDeleteModal) => {
    setOpenDeleteModal(newOpenDeleteModal)
  }

  const handleOpenUpdateModal = (newOpenUpdateModal) => {
    setOpenUpdateModal(newOpenUpdateModal)

    setOpenViewModal(CLOSE_MODAL)
  }

  const handleOpenViewModal = (newOpenViewModal) => {
    setOpenViewModal(newOpenViewModal)
  }

  const renderPagination = () => {
    if (pages < 2) {
      return
    }

    return (
      <Col className='align-center rupture-irc-pagination'>
        <Pagination
          count={count}
          current={current}
          getPage={(page) => setPagination({ ...pagination, page })}
          pages={pages}
        />
      </Col>
    )
  }

  const renderSaveModal = () => {
    if (!openSaveModal) {
      return
    }

    return (
      <SaveModal
        closeModal={() => handleOpenSaveModal(false)}
        dynamicList={formattedDynamicList}
        initialValues={{
          parcelRangeList: [
            {
              0: {
                cdParcel: 1,
                cdNewDynamic: '',
                prMaxValue: '',
                prMinValue: '',
              },
            },
          ],
        }}
        onSubmit={handleSave}
        parcelList={parcelList}
        title='config.pricebannerConfig.newButton'
        translate={translate}
      />
    )
  }

  const handleSave = async (values) => {
    await save(dsToken, values)

    setOpenSaveModal(false)

    handleFetch()
  }

  const handleUpdate = async (values) => {
    await save(dsToken, values)

    setOpenUpdateModal(false)

    handleFetch()
  }

  const renderSuccessModal = () => {
    if (!openSuccessModal.open) {
      return
    }

    return (
      <SuccessModal
        handleClose={() => setOpenSuccessModal(CLOSE_MODAL)}
        t={translate}
        msg={translate(openSuccessModal.message)}
        size='sm'
        center
        buttonText='OK'
      />
    )
  }

  const renderErrorModal = () => {
    if (!openErrorModal.open) {
      return
    }

    return (
      <ErrorModal
        closeModal={() => setOpenErrorModal(CLOSE_MODAL)}
        translate={translate}
      >
        <p>{openErrorModal.message}</p>
      </ErrorModal>
    )
  }

  const renderViewModal = () => {
    if (!openViewModal.open) {
      return
    }

    return (
      <ViewModal
        closeModal={() => setOpenViewModal(CLOSE_MODAL)}
        handleUpdate={() =>
          handleOpenUpdateModal({ open: true, record: openViewModal.record })
        }
        record={openViewModal.record}
        translate={translate}
      />
    )
  }

  const renderUpdateModal = () => {
    if (!openUpdateModal.open) {
      return
    }

    const { record } = openUpdateModal

    const getPrValue = (value) =>
      Mask.toMoney(value.toFixed(translate('size.currency')), {
        separator: translate('separator.currency'),
        delimiter: translate('delimiter.currency'),
      })

    const actualRecord = {
      ...record,
      dtPeriodEnd: moment(record.dtPeriodEnd),
      dtPeriodSt: moment(record.dtPeriodSt),
      parcelRangeList: record.parcelRangeList.map((parcelRange, index) => ({
        [index]: {
          ...parcelRange,
          prMaxValue: getPrValue(parcelRange.prMaxValue),
          prMinValue: getPrValue(parcelRange.prMinValue),
        },
      })),
    }

    return (
      <SaveModal
        closeModal={() => setOpenUpdateModal(CLOSE_MODAL)}
        dynamicList={formattedDynamicList}
        initialValues={actualRecord}
        onSubmit={handleUpdate}
        parcelList={parcelList}
        title='config.pricebannerConfig.editTitle'
        translate={translate}
      />
    )
  }

  const renderDeleteModal = () => {
    if (!openDeleteModal.open) {
      return
    }

    return (
      <DeleteModal
        closeModal={() => setOpenDeleteModal(CLOSE_MODAL)}
        remove={() => handleDelete(openDeleteModal.record)}
        translate={translate}
      />
    )
  }

  const handleDelete = async (record) => {
    await remove(dsToken, record)

    setOpenDeleteModal(CLOSE_MODAL)

    setOpenSuccessModal({
      open: true,
      message: 'config.pricebannerConfig.deleteSuccess',
    })

    handleFetch()
  }

  return (
    <>
      <ContentHeader>{translate('config.pricebannerConfig2')}</ContentHeader>
      {renderBreadcrumb()}
      <Grid className='pricebanner-config-container'>
        <Row>
          <Col
            xs={12}
            className='text-align-right margin-bottom-double margin-top'
          >
            <button
              onClick={() => handleOpenSaveModal(true)}
              type='button'
              className='button button-dark'
            >
              {translate('config.pricebannerConfig.newButton')}
            </button>
          </Col>
        </Row>
        {list.length ? (
          <Table
            auth={auth}
            handleDelete={handleOpenDeleteModal}
            handleUpdate={handleOpenUpdateModal}
            handleView={handleOpenViewModal}
            records={list}
            translate={translate}
          />
        ) : (
          <Notification
            message={translate('no.data.found')}
            className='full margin notification-warning'
          />
        )}
        {!!list.length && renderPagination()}
      </Grid>
      <ToastContainer newestOnTop />
      {renderSaveModal()}
      {renderSuccessModal()}
      {renderErrorModal()}
      {renderViewModal()}
      {renderUpdateModal()}
      {renderDeleteModal()}
    </>
  )
}

PriceBannerConfigContainer.propTypes = {
  auth: PropTypes.object.isRequired,
  count: PropTypes.number.isRequired,
  current: PropTypes.number.isRequired,
  error: PropTypes.object,
  dataSave: PropTypes.object,
  errorSave: PropTypes.object,
  fetch: PropTypes.func.isRequired,
  fetchParcelList: PropTypes.func.isRequired,
  fetchPricefyConfig: PropTypes.func.isRequired,
  list: PropTypes.array.isRequired,
  logout: PropTypes.func.isRequired,
  menu: PropTypes.object.isRequired,
  pages: PropTypes.number.isRequired,
  parcelList: PropTypes.array.isRequired,
  pricefyConfigError: PropTypes.object,
  pricefyConfigRecord: PropTypes.array.isRequired,
  remove: PropTypes.func.isRequired,
  save: PropTypes.func.isRequired,
  translate: PropTypes.func.isRequired,
}

const mapStateToProps = ({
  pricebannerConfigReducer,
  pricefyReducer,
  paginateReducer,
  parcelReducer,
}) => ({
  ...pricebannerConfigReducer,
  ...paginateReducer,
  parcelList: parcelReducer.list,
  pricefyConfigError: pricefyReducer.error,
  pricefyConfigRecord: pricefyReducer.record,
})

const mapDispatchToProps = (dispatch) =>
  bindActionCreators(
    {
      ...parcelActions,
      ...pricebannerConfigActions,
      ...pricefyActions,
    },
    dispatch
  )

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(
  translate(null, { wait: true, translateFuncName: 'translate' })(
    PriceBannerConfigContainer
  )
)

export { PriceBannerConfigContainer }
